import { useState } from "react";
import { api, errText } from "../../lib/api";
import { Badge, CustomerLink, DataTable, Select, Stat, money, shortDate, statusTone, useTable } from "./ui";

export default function SubscriptionsPage() {
  const table = useTable("/admin/tables/subscriptions");
  const [msg, setMsg] = useState("");

  const act = async (fn, done) => {
    setMsg("");
    try {
      await fn();
      setMsg(done);
      table.reload();
    } catch (e) {
      setMsg(errText(e));
    }
  };

  const columns = [
    { key: "subscription_number", label: "Plan", sortable: true, render: (r) => (
      <span className="whitespace-nowrap">{r.subscription_number}<span className="block text-xs text-muted">{r.plan}</span></span>
    ) },
    { key: "status", label: "Status", sortable: true, render: (r) => (
      <Badge tone={statusTone(r.status)}>{r.status}{r.cancel_at_period_end ? " · ending" : ""}</Badge>
    ) },
    { key: "buyer_email", label: "Buyer", render: (r) => <span className="text-xs"><CustomerLink email={r.buyer_email} /></span> },
    { key: "recipient_name", label: "Recipient", render: (r) => <span className="text-xs">{r.recipient_name}<span className="block text-muted">{r.city}</span></span> },
    { key: "gateway", label: "Gateway", render: (r) => <Badge tone="ink">{r.gateway}</Badge> },
    { key: "amount", label: "Each delivery", render: (r) => (
      <span className="tabular whitespace-nowrap">{r.symbol} {r.amount}{r.discount_percent ? ` −${r.discount_percent}%` : ""}</span>
    ) },
    { key: "next_delivery_date", label: "Next", sortable: true, render: (r) => <span className="tabular">{shortDate(r.next_delivery_date)}</span> },
    { key: "deliveries_done", label: "Sent", render: (r) => <span className="tabular">{r.deliveries_done}</span> },
    { key: "last_error", label: "Last error", render: (r) => (r.last_error ? <span className="text-xs text-danger">{r.last_error}</span> : "—") },
    { key: "actions", label: "", render: (r) => (["active", "payment_failed"].includes(r.status) ? (
      <button className="btn-ghost text-[11px] text-danger" data-testid={`cancel-plan-${r.subscription_number}`}
              onClick={() => act(() => api.post(`/admin/subscriptions/${r.id}/cancel`, { at_period_end: false }), "Plan cancelled.")}>
        Cancel now
      </button>
    ) : "—") },
  ];

  const counts = table.data?.counts || {};

  return (
    <div className="space-y-4" data-testid="admin-subscriptions">
      {msg ? <p className="text-sm text-brass" data-testid="subscriptions-message">{msg}</p> : null}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {Object.entries(counts).map(([k, v]) => (
          <Stat key={k} testid={`plans-${k}`} label={k.replace(/_/g, " ")} value={v} />
        ))}
      </div>
      <DataTable table={table} columns={columns} testid="subscriptions-table" name="recurring-plans"
                 searchPlaceholder="Plan number, buyer, recipient…" empty="No recurring plan matches that."
                 right={<button className="btn-ghost text-[11px]" data-testid="run-due-deliveries"
                                onClick={() => act(async () => { const { data } = await api.post("/admin/subscriptions/run-due"); setMsg(`Minted ${data.minted} · retrying ${data.retried} · skipped ${data.skipped}`); }, "")}>
                   Mint deliveries due now
                 </button>}
                 filters={(
                   <>
                     <Select testid="plans-filter-status" placeholder="Any status" value={table.params.status} onChange={(v) => table.set({ status: v })}
                             options={["active", "pending_payment", "payment_failed", "cancelled", "completed"]} />
                     <Select testid="plans-filter-plan" placeholder="Any cadence" value={table.params.plan} onChange={(v) => table.set({ plan: v })}
                             options={["weekly", "monthly", "yearly", "custom"]} />
                     <Select testid="plans-filter-gateway" placeholder="Any gateway" value={table.params.gateway} onChange={(v) => table.set({ gateway: v })}
                             options={["stripe", "tryb"]} />
                   </>
                 )} />
      <p className="text-xs text-muted">Monthly value of the active plans: {money((table.data?.rows || []).reduce((s, r) => s + (r.amount || 0), 0))} in the buyers' currencies.</p>
    </div>
  );
}
